import { createContext, ComponentChildren } from "preact";
import { useContext, useState, useEffect } from "preact/hooks";
import { AutoPlaySettings } from "../../hooks/useAutoPlay";

export type ModalType =
  | "myBets"
  | "myBetDetails"
  | "provablyFair"
  | "gameRules"
  | "onboarding"
  | "ProvablyFairDescription"
  | "language"
  // | "leaderboard"
  | "avatarSelector"
  | "gameLimits"
  | "reloadModal"
  | "skins";

interface ModalState {
  type: ModalType | null;
  data?: any;
}

interface ModalContextType {
  currentModal: ModalState;
  modalStack: ModalState[];
  openModal: (type: ModalType, data?: any) => void; // push
  closeModal: () => void; // pop
  isModalOpen: (type: ModalType) => boolean;
  isOpenBurger: boolean;
  toggleBurger: () => void;
  autoPlayStartHandler?: (settings: AutoPlaySettings) => void;
  setAutoPlayStartHandler: (
    handler: (settings: AutoPlaySettings) => void
  ) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

interface ModalProviderProps {
  children: ComponentChildren;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modalStack, setModalStack] = useState<ModalState[]>([]);
  const [isOpenBurger, setIsOpenBurger] = useState(false);
  const [autoPlayStartHandler, setAutoPlayStartHandler] = useState<
    ((settings: AutoPlaySettings) => void) | undefined
  >();

  const currentModal: ModalState = modalStack[modalStack.length - 1] || {
    type: null,
  };

  const toggleBurger = () => {
    setIsOpenBurger(!isOpenBurger);
  };

  const openModal = (type: ModalType, data?: any) => {
    setModalStack((prev) => [...prev, { type, data }]);
  };

  const closeModal = () => {
    setModalStack((prev) => prev.slice(0, -1));
  };

  const isModalOpen = (type: ModalType) => {
    return modalStack.some((m) => m.type === type);
  };

  // Lock body scroll while any modal is open
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (modalStack.length > 0) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [modalStack.length]);

  return (
    <ModalContext.Provider
      value={{
        currentModal,
        modalStack,
        isOpenBurger,
        openModal,
        closeModal,
        isModalOpen,
        toggleBurger,
        autoPlayStartHandler,
        setAutoPlayStartHandler,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
